import { FormErrorsAlert } from '@framework/form/form/errors/FormErrorsAlert.tsx';
import { FormHandler } from '@framework/form/form/FormHandler.tsx';
import { SubmitButton } from '@framework/form/form/submit/SubmitButton.tsx';
import { DrawerButtonRow } from '@framework/ui/components/drawer/DrawerButtonRow.tsx';
import { DividerTitleElement } from '@framework/ui/components/text/DividerTitleElement.tsx';
import { Button, Space } from 'antd';
import type * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type ObjectSchema } from 'yup';
import { type CreateCommunicationRequest } from '../../../schema/communication/CreateCommunicationRequestSchema.ts';
import { useCommunicationsContext } from '../context/CommunicationsContext.tsx';
import { CommunicationDetailForm } from './CommunicationDetailForm.tsx';
import { CommunicationSendButton } from './CommunicationSendButton.tsx';
import { CommunicationTypeStaticInput } from './CommunicationTypeStaticInput.tsx';
import { CommunicationTypeSelectInput } from './components/CommunicationTypeSelectInput.tsx';

export interface CommunicationFormProps {
  communication?: CreateCommunicationRequest | null;
  validationSchema: ObjectSchema<CreateCommunicationRequest>;
  onClose?: (e?: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  /** visualizzazione alternativa tipo email */
  emailLike?: boolean;
}

/**
 * Form di creazione di una comunicazione
 */
export function CommunicationForm(props: CommunicationFormProps) {
  const { t } = useTranslation();
  const { communication, validationSchema, onClose } = props;

  const [isLoading, setIsLoading] = useState(false);
  const { editableTypes, loadableTypes, sendableTypes, onSave } =
    useCommunicationsContext();

  // Le tipologie di comunicazione sono diverse a seconda che si tratti di
  // una comunicazione caricata manualmente o creata.
  // Quando si carica manualmente una comunicazione, si possono selezionare
  // solo quelle _Caricabili_ (azione Carica o Gestisci sullo user task).
  const availableTypes = useMemo(
    () => (communication?.isManual ? loadableTypes : editableTypes),
    [communication?.isManual, editableTypes, loadableTypes],
  );

  const isNew = communication?.idComunicazione == null;
  const isManual = !!communication?.isManual;

  const initialValues = useMemo(() => {
    return communication ?? {};
  }, [communication]);

  const validationContext = useMemo(() => {
    return {
      isEdit: communication?.idComunicazione != null,
    };
  }, [communication?.idComunicazione]);

  const handleSubmit = useCallback(
    async (data: CreateCommunicationRequest) => {
      setIsLoading(true);
      const result = await onSave?.(data, data?.isManual, {
        saveOnly: data?.saveOnlyFlag,
      });
      setIsLoading(false);
      return result;
    },
    [onSave],
  );

  const hasForwarded = Boolean(communication?.inoltro?.idComunicazione);

  return (
    <FormHandler
      id="communication"
      layout="vertical"
      nested
      schema={validationSchema as any}
      validationContext={validationContext}
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      <div>
        <FormErrorsAlert />
        <DividerTitleElement
          title={t('communications.type-title', 'Tipo di Comunicazione')}
          removeTopMargin
        />

        {isNew ? (
          <CommunicationTypeSelectInput availableTypes={availableTypes} />
        ) : (
          <CommunicationTypeStaticInput
            communication={communication}
            isNew={isNew}
          />
        )}

        <CommunicationDetailForm
          communication={communication}
          isManual={isManual}
          hasForwarded={hasForwarded}
        />

        <DrawerButtonRow>
          <Space>
            <Button
              size="large"
              onClick={e => {
                onClose?.(e);
              }}
            >
              {t('interazioni.annulla', 'Annulla')}
            </Button>
            <SubmitButton size="large" type="primary" loading={isLoading}>
              {t('interazioni.salva', 'Salva')}
            </SubmitButton>
            <CommunicationSendButton
              sendableTypes={sendableTypes}
              isManual={isManual}
              loading={isLoading}
              setIsLoading={setIsLoading}
            />
          </Space>
        </DrawerButtonRow>
      </div>
    </FormHandler>
  );
}
